/**
 * When practising something closes the mistake that produced it.
 *
 * `mistakes.close_modality` carries the rule — *"the modality it must be passed in to
 * close; a pronunciation error is never closed by a multiple-choice question"* — and the
 * screen spec states it as *"a mistake closes only when the same item is passed in the
 * modality it was failed in"* (docs/04-screen-specs.md §C6). Both were written; neither
 * was ever evaluated, because the SQL that closed a mistake matched on `item_id`, a
 * column no writer has ever set. Every mistake the product has recorded is still open,
 * and `attempts` has never once been incremented.
 *
 * The two functions here are the rule itself, kept out of the route handler because that
 * is where it stops being reviewable (CLAUDE.md: domain logic goes in `packages/core`).
 */

import type { Modality } from '../types.js';
import type { DrillExerciseType } from './assemble.js';

/**
 * Which review modality an exercise actually exercises.
 *
 * One row per exercise type with the reason, following the convention in `games/`, since
 * the mapping is a pedagogical claim rather than a lookup: it decides what a learner has
 * proved by getting one right, and therefore which mistakes it is allowed to close.
 */
const MODALITY_BY_EXERCISE: Record<DrillExerciseType, Modality> = {
  // Picked the right answer from answers on screen. Proves it was recognised, no more.
  mcq: 'recognition',
  match: 'recognition',
  // Supplied the missing piece from nothing. Written production, which `cloze` is.
  fillBlank: 'cloze',
  reorder: 'cloze',
  // Rewrote their own sentence correctly — the mistake drill's own exercise.
  transform: 'cloze',
  // Heard it and wrote it down, which is the written form under test, not comprehension.
  dictation: 'spelling',
  // Said it. `production` and `minimalPair` are the two the domain calls spoken, and
  // these are the only exercise types that put a microphone in front of the learner.
  shadowing: 'production',
  minimalPair: 'minimalPair',
};

export function modalityOfExercise(type: DrillExerciseType): Modality {
  return MODALITY_BY_EXERCISE[type];
}

/**
 * Whether passing in `passedIn` is allowed to close a mistake demanding `closeModality`.
 *
 * Null means no constraint — the mistake was recorded without one, which is the case for
 * everything the conversation analyser mines — so any modality closes it. Otherwise the
 * match is exact, which is what both the column comment and the screen spec say.
 *
 * Exact rather than "spoken closes spoken" on purpose. A phoneme error is recorded with
 * `minimalPair`, and `shadowing` is also spoken, so a looser rule would let repeating a
 * word after a model close a mistake about telling two sounds apart. Those are different
 * skills, and the contrast is the one the clinic is built around.
 */
export function closesMistake(closeModality: Modality | null, passedIn: Modality): boolean {
  return closeModality === null || closeModality === passedIn;
}

/**
 * Whether a typed answer counts as the expected one.
 *
 * The exercise a mistake drill actually builds is `transform`: the learner's own wrong
 * sentence as the prompt, the tutor's correction as the answer. Grading it is a string
 * comparison, and the whole question is which differences are the lesson and which are
 * noise.
 *
 *  - **Case is noise.** The correction is stored however the tutor wrote it, often
 *    sentence-cased, and a learner typing in lower case has not made the mistake again.
 *  - **Punctuation and spacing are noise.** A missing full stop is not the grammar error
 *    the drill was built from, and failing someone for it teaches them nothing.
 *  - **Accents are the lesson, not noise.** `esta`/`está` and `ano`/`año` are different
 *    words, so this compares NFC-normalised text and never strips diacritics. Folding
 *    them would silently mark a Spanish learner correct for the error the course exists
 *    to fix.
 */
export function matchesWrittenAnswer(expected: string, given: string): boolean {
  const canonical = (text: string) =>
    text
      .normalize('NFC')
      .toLocaleLowerCase()
      // Punctuation and symbols only: letters, numbers and marks survive, so a combining
      // accent is kept even when it is stored separately from its vowel.
      .replace(/[\p{P}\p{S}]/gu, '')
      .replace(/\s+/gu, ' ')
      .trim();

  const target = canonical(expected);
  // An empty expectation would match an empty answer and mark a blank as correct.
  return target.length > 0 && canonical(given) === target;
}
